import React, { useState, useEffect, useRef, useContext } from "react";
import mapboxgl from "mapbox-gl";
import { Button, Drawer, Icon, IconButton, Paper } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { store } from "../contexts/store";
import {
  fillColors,
  legendItemColors,
  mapStyle,
  RASTER_URL,
  fillColors8Stops,
  addPlaceLabelLayers,
} from "../util/mapUtils";
import {
  setFBILevel,
  setFeature,
  setFeatures,
  setLocation,
  setZoom,
  uploadFeature,
} from "../contexts/actions";
import turf from "turf";
import useMediaQuery from "@mui/material/useMediaQuery";
import { filenames, renderParam, configObj } from "../config";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

const LOCALSTORAGE_KEYS = {
  accessToken: "map_access_token",
};
mapboxgl.accessToken =
  "pk.eyJ1IjoiZ2lyaXNocGFsbGFnYXR0aSIsImEiOiJja2h6NGVuc20wYndjMnlxaDExbzlsa3B2In0.e94ZfcoPleAA2nqjeX10ag";

const TILE_URL =
  "https://vectors.digifarm.io/development/vectors/test/test/{z}/{x}/{y}.mvt";

const Map = ({ onFieldClick }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const mobileView = useMediaQuery("(max-width:600px)");
  const sustIndLayer = "sust_ind_layer";
  const {
    state: {
      location,
      features,
      layers,
      fbiLevel,
      indexName,
      uploadFeature,
      table,
      year,
      cropYear,
      season,
    },
    dispatch,
  } = useContext(store);

  const get6values = (min, max) => {
    const minVal = parseFloat(min);
    const maxVal = parseFloat(max);
    const diff = maxVal - minVal;
    const step = diff / 6;
    const values = [];
    for (let i = 0; i < 6; i++) {
      values.push(minVal + step * i);
    }
    values.push(maxVal);
    return values;
  };

  const getColorStops = () => {
    return fbiLevel === "local"
      ? configObj[year].localMinMax[indexName].map((item, index) => [
          parseFloat(item),
          indexName === "BsiInd"
            ? fillColors[
                configObj[year].localMinMax[indexName].length - 1 - index
              ]
            : fillColors[index],
        ])
      : [-2, -1, -0.5, 0, 0.5, 1, 1.5, 2.15].map((item, index) => [
          parseFloat(item),
          indexName === "BsiInd"
            ? fillColors8Stops[8 - 1 - index]
            : fillColors8Stops[index],
        ]);
  };

  let selectedFieldId = null;

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: location.center,
      zoom: location.zoom,
      hash: true,
      maxZoom: 16.6,
    });

    map.on("load", () => {
      addPlaceLabelLayers(map);

      if (filenames) {
        filenames.forEach((file, index) => {
          const sourceId = `basemap_geotiff_${index}`;
          map.addSource(sourceId, {
            type: "raster",
            tiles: [
              RASTER_URL +
                "&filename=" +
                file +
                (renderParam ? "&render=" + renderParam : ""),
            ],
            tileSize: 256,
          });
          map.addLayer({
            id: sourceId,
            source: sourceId,
            type: "raster",
            minzoom: 0,
            maxzoom: 24,
          });
        });
      }

      if (indexName === "timeseries") {
        const paintStyle = { "icon-color": "#01B075" };

        map.addLayer({
          id: "markers",
          type: "symbol",
          source: "feature",
          minzoom: 0,
          maxzoom: 5.4,
          layout: {
            "icon-image": "coverageMarker",
          },
          paint: paintStyle,
        });
        map.on("click", "markers", (e) => {
          if (map.getZoom() < 5.4) {
            map.flyTo({ center: e.lngLat, zoom: 5.4 });
          }
        });
      }

      if (indexName === "SustInd") {
        map.addSource(sustIndLayer, {
          type: "vector",
          tiles: [TILE_URL + `?table=${configObj[year].table}` + `&token=${window.localStorage.getItem(LOCALSTORAGE_KEYS.accessToken)}`],
        });

        const sustValues = get6values(
          configObj[year].localMinMax[indexName][0],
          configObj[year].localMinMax[indexName][
            configObj[year].localMinMax[indexName].length - 1
          ]
        );

        map.addLayer({
          id: `${sustIndLayer}_line`,
          source: sustIndLayer,
          "source-layer": "test",
          type: "line",
          paint: {
            "line-width": 1,
            "line-color": "#fff",
          },
        });

        const colorStops3 = getColorStops();

        map.addLayer({
          id: sustIndLayer,
          type: "fill",
          source: sustIndLayer,
          "source-layer": "test",
          paint: {
            "fill-color": {
              property: indexName,
              type: "exponential",
              stops: [...colorStops3],
              base: 1,
            },
          },
        });
      }
    });

    !mobileView &&
      map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

    map.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        types: "country,region,district,place,locality,postcode",
        collapsed: true,
        marker: {
          color: "#01B075",
        },
      })
    );

    setMap(map);
    mapRef.current = map;

    // Clean up on unmount
    return () => map.remove();
  }, []);

  useEffect(() => {
    if (!map) return;

    const handleMapClick = (e) => {
      if (
        map.getFeatureState({
          source: sustIndLayer,
          sourceLayer: "test",
          id: selectedFieldId,
        }).selected
      ) {
        map.removeFeatureState(
          {
            source: sustIndLayer,
            sourceLayer: "test",
            id: selectedFieldId,
          },
          "selected"
        );
      }
      // console.log("e.features[0]: ", e.features[0]);
      selectedFieldId = e.features[0].id;
      map.setFeatureState(
        {
          source: sustIndLayer,
          sourceLayer: "test",
          id: selectedFieldId,
        },
        { selected: true }
      );

      var queriedSourceFeatures = map.querySourceFeatures(sustIndLayer, {
        sourceLayer: "test",
        filter: ["==", ["id"], selectedFieldId],
      });

      var combinedPolygon = queriedSourceFeatures.reduce(function (
        accumulator,
        currentValue
      ) {
        return turf.union(accumulator, currentValue);
      });

      combinedPolygon.id = selectedFieldId;

      dispatch({
        type: setFeature,
        payload: { feature: e.features ? combinedPolygon : null },
      });

      if (indexName === "timeseries") {
        onFieldClick(combinedPolygon); // Trigger onFieldClick when indexName is "timeseries"

        if (map.getSource("selected-feature-timeseries")) {
          map.removeLayer("selected-feature-timeseries");
          map.removeSource("selected-feature-timeseries");
        }
        map.addSource("selected-feature-timeseries", {
          type: "geojson",
          data: {
            ...features,
            features: features.filter(
              (feat) => feat.id === e.features[0].properties.id
            ),
          },
        });

        map.addLayer({
          id: "selected-feature-timeseries",
          type: "fill",
          source: "selected-feature-timeseries",
          paint: {
            "fill-color": "rgba(234, 137, 29, 0.8)",
          },
        });
      }
    };

    map.on("click", sustIndLayer, handleMapClick);

    // Clean up the event listener on component unmount or when indexName changes
    return () => {
      map.off("click", sustIndLayer, handleMapClick);
    };
  }, [map, indexName]);

  useEffect(() => {
    const colorStops3 = getColorStops();
    map &&
      map.setPaintProperty(sustIndLayer, "fill-color", {
        property: indexName,
        type: "exponential",
        stops: [...colorStops3],
        base: 1,
      });
  }, [fbiLevel]);

  useEffect(() => {
    if (
      map &&
      indexName !== "fields" &&
      indexName !== "Deforestation" &&
      indexName !== "crop" &&
      indexName !== "timeseries"
    ) {
      const colorStops3 = getColorStops();
      map.setPaintProperty(sustIndLayer, "fill-color", {
        property: indexName,
        type: "exponential",
        stops: [...colorStops3],
        base: 1,
      });
      map.setPaintProperty(`${sustIndLayer}_line`, "line-width", 1);
    } else if (map && indexName == "Deforestation") {
      map.setPaintProperty(sustIndLayer, "fill-color", [
        "case",
        ["==", ["get", "not_forest"], true], // If the property is true
        "#CE4236", // Red color
        "#1F8B4D", // Green color otherwise
      ]);
    } else if (
      (map && indexName === "fields") ||
      (map && indexName === "timeseries")
    ) {
      map.setPaintProperty(sustIndLayer, "fill-color", "rgba(0, 0, 0, 0)");
      map.setPaintProperty(`${sustIndLayer}_line`, "line-width", 2);
    } else if (map && indexName === "crop") {
      map.setPaintProperty(sustIndLayer, "fill-color", [
        "match",
        ["get", "predicted_crop"],
        "TOMATO",
        "#F3B670",
        "MANGO",
        "#EC8577",
        "CITRUS",
        "#C0DD32",
        "unknown",
        "#CCCCCC",
        "#CCCCCC",
      ]);
    }
  }, [indexName]);

  function getBBOX(geojson) {
    const bbox = [
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    ];

    let dimensions = 2;
    for (const coordinate of coordEach(geojson)) {
      dimensions = Math.max(dimensions, coordinate.length);
      for (let i = 0; i < coordinate.length; ++i) {
        const j = 3 + i;
        bbox[i] = Math.min(bbox[i], coordinate[i]);
        bbox[j] = Math.max(bbox[j], coordinate[i]);
      }
    }

    return [bbox[0], bbox[1], bbox[3], bbox[4]];
  }

  function* coordEach(geojson) {
    switch (geojson.type) {
      case "Point":
        yield geojson.coordinates;
        break;
      case "LineString":
      case "MultiPoint":
        yield* geojson.coordinates;
        break;
      case "Polygon":
      case "MultiLineString":
        for (const part of geojson.coordinates) {
          yield* part;
        }
        break;
      case "MultiPolygon":
        for (const polygon of geojson.coordinates) {
          for (const ring of polygon) {
            yield* ring;
          }
        }
        break;
      case "GeometryCollection":
        for (const geometry of geojson.geometries) {
          yield* coordEach(geometry);
        }
        break;
      case "FeatureCollection":
        for (const feature of geojson.features) {
          yield* coordEach(feature);
        }
        break;
      default:
        yield* coordEach(geojson.geometry);
    }
  }

  useEffect(() => {
    if (map && uploadFeature) {
      if (map.getSource("uploaded-polygon")) {
        map.removeLayer("uploaded-polygon");
        map.removeSource("uploaded-polygon");
      }
      map.addSource("uploaded-polygon", {
        type: "geojson",
        data: uploadFeature,
        generateId: true,
      });

      map.addLayer({
        id: "uploaded-polygon",
        type: "line",
        source: "uploaded-polygon",
        paint: {
          "line-color": "rgba(255,255,255,0.6)",
          "line-width": 2,
        },
      });

      map.fitBounds(getBBOX(uploadFeature));
    }
  }, [uploadFeature]);

  useEffect(() => {
    if (map && location) {
      map.flyTo({
        center: location.center,
        zoom: location.zoom ? location.zoom : 14,
      });
    }
  }, [location]);

  useEffect(() => {
    const newTileUrl = TILE_URL + `?table=${configObj[year].table}`;

    if (map && indexName !== "Deforestation" && indexName !== "crop") {
      map.getSource(sustIndLayer) &&
        map.getSource(sustIndLayer).setTiles([newTileUrl]);
    } else if (map && indexName === "Deforestation") {
      const newTileUrl = TILE_URL + `?table=${configObj[2024].table}`;
      map.getSource(sustIndLayer) &&
        map.getSource(sustIndLayer).setTiles([newTileUrl]);
    } else if (map && indexName === "crop") {
      const newTileUrl = TILE_URL + `?table=${configObj[year].table}`;
      map.getSource(sustIndLayer) &&
        map.getSource(sustIndLayer).setTiles([newTileUrl]);
    }
  }, [year, indexName]);

  useEffect(() => {
    if (map && indexName === "crop") {
      const newTileUrl =
        TILE_URL + `?table=${configObj["cropTables"][season][cropYear]}`;
      map.getSource(sustIndLayer) &&
        map.getSource(sustIndLayer).setTiles([newTileUrl]);
      map.setPaintProperty(sustIndLayer, "fill-color", [
        "match",
        ["get", "predicted_crop"],
        "TOMATO",
        "#F3B670",
        "MANGO",
        "#EC8577",
        "CITRUS",
        "#C0DD32",
        "unknown",
        "#CCCCCC",
        "#CCCCCC",
      ]);
    }
  }, [cropYear, season, indexName]);

  return (
    <div className="map-and-controls-container">
      <div className="map-container" ref={mapContainerRef} />
    </div>
  );
};

export default Map;
